import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, Home, LayoutDashboard, Settings } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

interface AppHeaderProps {
  navLinks?: NavLink[];
  showSettings?: boolean;
}

export function AppHeader({ navLinks, showSettings = false }: AppHeaderProps) {
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const getInitials = (email: string) => {
    return (email.split("@")[0] ?? "").slice(0, 2).toUpperCase();
  };

  const defaultNavLinks: NavLink[] = [
    { to: "/", label: "Home", icon: <Home className="mr-2 h-4 w-4" /> },
  ];

  const links = navLinks ?? defaultNavLinks;

  return (
    <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-sm">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.svg"
              alt="Logo"
              className="h-8 w-8"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="font-semibold">HolyMolyLab</span>
          </Link>
          <nav className="hidden md:flex items-center gap-2">
            {links.map((link) => (
              <Link key={link.to} to={link.to}>
                <Button variant="ghost" size="sm">
                  {link.icon}
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          {showSettings && (
            <Link to="/profile">
              <Button variant="ghost" size="sm">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
            </Link>
          )}
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback>
                {user?.email ? getInitials(user.email) : "U"}
              </AvatarFallback>
            </Avatar>
            <span className="hidden md:inline text-sm text-muted-foreground">
              {user?.email}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
